import { useAuthenticationStore } from '@/stores/authentication'
import { useProtobufStore } from '@/stores/protobuf'

import type { Ref } from 'vue'

export interface SchemaKey {
  /**
   * The program slug.
   */
  slug: string
  /**
   * The version of the current state, if applicable.
   */
  version?: string
}

export interface SchemaKeyDetails {
  /**
   * The current key.
   */
  key: SchemaKey
  /**
   * Whether a new schema should be loaded.
   * This is a combination of logic:
   * - If no slug exists this is always false
   * - If `data` param is falsy this is always true
   * - Otherwise this is true only if the cached key does not match
   */
  shouldLoad: boolean
}

export interface UseSchemaKeyParams<T> {
  /**
   * A ref pointing to the loaded data.
   * If falsy then `shouldLoad` will return true even if the key is cached.
   */
  data: Ref<T>
}

export interface UseSchemaKeyReturnValue {
  /**
   * Returns the current key to use for schema service requests.
   * If `shouldLoad` is `true` then the new data should not be loaded.
   *
   * Does not update the existing cached key, use `updateKey` after the request to do so.
   */
  getKey (): SchemaKeyDetails
  /**
   * Updates the cached key.
   * Should be called after a successful schema load.
   */
  updateKey (newKey: SchemaKey): void
}

/**
 * Creates a schema cache key and provides methods for interacting with it.
 */
export function useSchemaKey<T> (params: UseSchemaKeyParams<T>): UseSchemaKeyReturnValue {
  const cachedKey: SchemaKey = {
    slug: '',
  }

  const authenticationStore = useAuthenticationStore()
  const protobufStore = useProtobufStore()

  function checkShouldLoad (key: SchemaKey): boolean {
    if (!key.slug) return false
    if (!params.data.value) return true
    const cached = cachedKey.slug === key.slug && cachedKey.version === key.version
    return !cached
  }
  const getKey: UseSchemaKeyReturnValue['getKey'] = () => {
    const key: SchemaKey = {
      slug: authenticationStore.slug,
      version: protobufStore.policyState.program?.version || undefined,
    }
    return {
      key,
      shouldLoad: checkShouldLoad(key),
    }
  }

  const updateKey: UseSchemaKeyReturnValue['updateKey'] = (newKey) => {
    cachedKey.slug = newKey.slug
    cachedKey.version = newKey.version
  }

  return {
    getKey,
    updateKey,
  }
}
